import { format } from "date-fns";
import { es } from "date-fns/locale";
import type { Order } from "@/types/orders";
import { COMPANY_FISCAL_DATA, LOCATION_FISCAL_DATA, IGIC_RATE } from "@/constants/fiscal";

// Type for print mode: "cuenta" (bill, unpaid) or "ticket" (receipt, paid)
export type PrintMode = "cuenta" | "ticket";

interface OrderItemWithCustomization {
  id: string;
  order_id: string;
  product_name: string;
  product_category?: string;
  unit_price: number;
  quantity: number;
  subtotal: number;
  created_at: string;
  removed_ingredients?: string[] | null;
}

interface OrderWithLocation extends Order {
  locations?: { id: string; code: string; name: string } | null;
  items?: OrderItemWithCustomization[];
  cash_received?: number;
  change_returned?: number;
  ticket_series?: string;
  series_number?: number;
}

// Helper to escape HTML to prevent XSS
const escapeHtml = (text: string | null | undefined): string => {
  if (!text) return "";
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

// Generate simulated auth code for local card payments
const generateAuthCode = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Generate the complete HTML string for a thermal ticket or cuenta (bill)
 * @param order - The order data
 * @param mode - "ticket" for paid receipt, "cuenta" for unpaid bill
 */
export function generateTicketHTML(order: OrderWithLocation, mode: PrintMode = "ticket"): string {
  const isCuenta = mode === "cuenta";
  const formatDate = (dateStr: string) => format(new Date(dateStr), "dd/MM/yy", { locale: es });
  const formatTime = (dateStr: string) => format(new Date(dateStr), "HH:mm", { locale: es });
  const formatDateTime = (dateStr: string) => format(new Date(dateStr), "dd/MM/yyyy HH:mm:ss", { locale: es });

  // Get location data
  const locationCode = order.locations?.code || "siete-palmas";
  const locationFiscal = LOCATION_FISCAL_DATA[locationCode] || LOCATION_FISCAL_DATA["siete-palmas"];

  const year = new Date(order.created_at).getFullYear();
  
  // Use new ticket series format if available, fallback to legacy format
  const invoiceNumber = order.ticket_series && order.series_number
    ? `${order.ticket_series}-${year}-${order.series_number.toString().padStart(5, "0")}`
    : `${locationFiscal.serieFactura}-${year}-${order.order_number.toString().padStart(8, "0")}`;

  // Tax calculations
  const totalWithTax = order.total;
  const baseImponible = totalWithTax / (1 + IGIC_RATE);
  const cuotaIGIC = totalWithTax - baseImponible;

  // Loyalty mock
  const pointsEarned = Math.floor(order.total);
  const loyaltyMemberNumber = order.customer_phone ? "****" + order.customer_phone.slice(-4) : "****0000";
  const totalPoints = pointsEarned + 113;

  const getOrderTypeText = () => {
    if (order.order_type === "mesa") return `Mesa Nº ${order.table_number}`;
    if (order.order_type === "llevar") return "Para Llevar";
    return "Encargo";
  };

  const getPaymentMethodText = () => {
    if (order.payment_method === "online") return "TARJETA (Online)";
    if (!order.cash_received && order.payment_status === "pagado") return "TARJETA (Local)";
    return "EFECTIVO";
  };

  const isLocalCardPayment = order.payment_method !== "online" && 
    !order.cash_received && 
    order.payment_status === "pagado";

  // Generate items HTML
  const itemsHTML = order.items && order.items.length > 0
    ? order.items.map(item => `
        <div style="display: flex; justify-content: space-between; padding: 3px 0; font-size: 10px;">
          <span style="flex: 1; overflow: hidden;">${escapeHtml(item.product_name)}</span>
          <span style="width: 30px; text-align: center;">${item.quantity}</span>
          <span style="width: 50px; text-align: right;">${(item.quantity * item.unit_price).toFixed(2)}€</span>
        </div>
        ${item.removed_ingredients && item.removed_ingredients.length > 0 
          ? `<div style="font-size: 9px; color: #e65100; font-weight: bold; margin-left: 8px; margin-bottom: 2px;">
              (${item.removed_ingredients.map(i => `Sin ${i}`).join(", ")})
            </div>` 
          : ''}
      `).join('')
    : '<div style="padding: 3px 0; font-size: 10px;">Sin artículos</div>';

  // Served by line
  const servedByHTML = order.served_by 
    ? `<div style="display: flex; justify-content: space-between; margin: 2px 0;">
        <span>Atendido por:</span>
        <span style="font-weight: bold;">${escapeHtml(order.served_by)}</span>
      </div>`
    : order.payment_method === "online"
    ? `<div style="display: flex; justify-content: space-between; margin: 2px 0;">
        <span>Atendido por:</span>
        <span style="font-weight: bold;">Web</span>
      </div>`
    : '';

  // Customer name
  const customerNameHTML = order.customer_name 
    ? `<div style="font-size: 10px; text-align: center; margin-bottom: 4px;">
        Cliente: ${escapeHtml(order.customer_name)}
      </div>`
    : '';

  // Cash breakdown
  const cashBreakdownHTML = order.cash_received && order.change_returned !== undefined
    ? `<div style="border-top: 1px dashed #000; margin: 6px 0;"></div>
       <div style="padding: 4px 0;">
         <div style="display: flex; justify-content: space-between; font-size: 11px; padding: 2px 0;">
           <span>ENTREGADO:</span>
           <span>${order.cash_received.toFixed(2)}€</span>
         </div>
         <div style="display: flex; justify-content: space-between; font-size: 11px; padding: 2px 0; font-weight: bold; color: #2e7d32;">
           <span>CAMBIO:</span>
           <span>${order.change_returned.toFixed(2)}€</span>
         </div>
       </div>`
    : '';

  // Pickup info for encargos
  const pickupInfoHTML = order.order_type === "encargo" && order.pickup_date
    ? `<div style="border-top: 1px dashed #000; margin: 6px 0;"></div>
       <div style="text-align: center; padding: 6px 0;">
         <div style="font-weight: bold; font-size: 10px;">📅 RECOGIDA</div>
         <div style="font-size: 12px; font-weight: bold; margin: 2px 0;">
           ${escapeHtml(order.pickup_date)} ${escapeHtml(order.pickup_time || "")}
         </div>
         ${order.pickup_location ? `<div style="font-size: 10px;">📍 ${escapeHtml(order.pickup_location)}</div>` : ''}
       </div>`
    : '';

  // Observations
  const observationsHTML = order.observations
    ? `<div style="padding: 6px; background: #f5f5f5; font-size: 9px; margin: 6px 0;">
         <strong>Obs:</strong> ${escapeHtml(order.observations)}
       </div>`
    : '';

  // Online card receipt
  const onlineCardReceiptHTML = order.payment_method === "online" && order.stripe_session_id
    ? `<div style="border-top: 2px double #000; margin: 6px 0;"></div>
       <div style="text-align: center; padding: 6px 0;">
         <div style="font-weight: bold; font-size: 10px; margin-bottom: 2px;">CUSTOMER RECEIPT</div>
         <div style="font-size: 10px; margin-bottom: 4px;">Card payment APPROVED</div>
         <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>
         <div style="text-align: left; padding: 4px 8px; font-size: 9px;">
           <div style="display: flex; justify-content: space-between; padding: 1px 0;"><span>Date/Time:</span><span>${formatDateTime(order.created_at)}</span></div>
           <div style="display: flex; justify-content: space-between; padding: 1px 0;"><span>Merchant ID:</span><span>${COMPANY_FISCAL_DATA.cif}</span></div>
           <div style="display: flex; justify-content: space-between; padding: 1px 0;"><span>Terminal ID:</span><span>${locationFiscal.tpvId}</span></div>
           <div style="display: flex; justify-content: space-between; padding: 1px 0;"><span>Auth Code:</span><span>${order.stripe_session_id.slice(-8).toUpperCase()}</span></div>
         </div>
         <div style="font-size: 8px; margin-top: 4px; font-style: italic;">Please retain for your records</div>
       </div>`
    : '';

  // Local TPV card receipt
  const localCardReceiptHTML = isLocalCardPayment
    ? `<div style="border-top: 2px double #000; margin: 6px 0;"></div>
       <div style="text-align: center; padding: 6px 0;">
         <div style="font-weight: bold; font-size: 10px; margin-bottom: 2px;">RECIBO TPV</div>
         <div style="font-size: 10px; margin-bottom: 4px;">Pago con tarjeta APROBADO</div>
         <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>
         <div style="text-align: left; padding: 4px 8px; font-size: 9px;">
           <div style="display: flex; justify-content: space-between; padding: 1px 0;"><span>Fecha/Hora:</span><span>${formatDateTime(order.updated_at || order.created_at)}</span></div>
           <div style="display: flex; justify-content: space-between; padding: 1px 0;"><span>Comercio:</span><span>${COMPANY_FISCAL_DATA.cif}</span></div>
           <div style="display: flex; justify-content: space-between; padding: 1px 0;"><span>Terminal:</span><span>${locationFiscal.tpvId}</span></div>
           <div style="display: flex; justify-content: space-between; padding: 1px 0;"><span>Cod. Aut.:</span><span>${generateAuthCode()}</span></div>
           <div style="display: flex; justify-content: space-between; padding: 1px 0;"><span>Tarjeta:</span><span>************4242</span></div>
           <div style="display: flex; justify-content: space-between; padding: 1px 0; font-weight: bold;"><span>Importe:</span><span>${order.total.toFixed(2)}€</span></div>
         </div>
         <div style="font-size: 8px; margin-top: 4px; font-style: italic;">Conserve este recibo</div>
       </div>`
    : '';

  // Title based on mode
  const documentTitle = isCuenta ? "CUENTA" : "TICKET DE COMPRA";
  const documentSubtitle = isCuenta ? "Pendiente de pago" : "";

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${documentTitle} ${invoiceNumber}</title>
  <style>
    @page { 
      size: 80mm auto; 
      margin: 0; 
    }
    @media print {
      body { 
        margin: 0; 
        padding: 0; 
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
    body {
      font-family: 'Courier New', 'Lucida Console', monospace;
      font-size: 11px;
      line-height: 1.3;
      color: #000;
      background: #fff;
      margin: 0;
      padding: 0;
    }
    .ticket {
      width: 80mm;
      padding: 2mm;
    }
  </style>
</head>
<body>
  <div class="ticket">
    <!-- DOCUMENT TYPE HEADER (CUENTA vs TICKET) -->
    <div style="text-align: center; padding: 8px 0; margin-bottom: 4px; ${isCuenta ? 'background: #fff3cd; border: 2px dashed #ffc107;' : ''}">
      <div style="font-size: 18px; font-weight: bold; letter-spacing: 2px;">
        ${isCuenta ? '📝' : '🧾'} ${documentTitle}
      </div>
      ${isCuenta ? `<div style="font-size: 12px; color: #856404; margin-top: 4px; font-weight: bold;">⚠️ ${documentSubtitle}</div>` : ''}
    </div>

    <!-- HEADER -->
    <div style="text-align: center; padding-bottom: 6px;">
      <div style="font-size: 16px; font-weight: bold; letter-spacing: 1px; margin-bottom: 2px;">
        🥐 PASTELERÍA REYES
      </div>
      <div style="font-size: 9px; font-weight: bold; margin-bottom: 2px;">
        ${COMPANY_FISCAL_DATA.razonSocial}
      </div>
      <div style="font-size: 10px; font-weight: bold;">
        CIF: ${COMPANY_FISCAL_DATA.cif}
      </div>
    </div>

    <div style="border-top: 2px double #000; margin: 6px 0;"></div>

    <!-- STORE INFO -->
    <div style="text-align: center; font-size: 9px; padding: 4px 0; line-height: 1.4;">
      <div style="font-weight: bold; font-size: 10px;">${escapeHtml(locationFiscal.name)}</div>
      <div>${escapeHtml(locationFiscal.fullAddress || locationFiscal.address)}</div>
      <div>Tel: ${escapeHtml(locationFiscal.phone)}</div>
    </div>

    <div style="border-top: 2px double #000; margin: 6px 0;"></div>

    <!-- TICKET DATA -->
    <div style="padding: 6px 0; font-size: 10px;">
      <div style="display: flex; justify-content: space-between; margin: 2px 0;">
        <span>Nº Ticket:</span>
        <span style="font-weight: bold;">${invoiceNumber}</span>
      </div>
      <div style="display: flex; justify-content: space-between; margin: 2px 0;">
        <span>Fecha:</span>
        <span>${formatDate(order.created_at)}</span>
      </div>
      <div style="display: flex; justify-content: space-between; margin: 2px 0;">
        <span>Hora:</span>
        <span>${formatTime(order.created_at)}</span>
      </div>
      <div style="display: flex; justify-content: space-between; margin: 2px 0;">
        <span>N.TPV:</span>
        <span>${locationFiscal.tpvId}</span>
      </div>
      ${servedByHTML}
    </div>

    <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>

    <!-- ORDER TYPE -->
    <div style="font-weight: bold; font-size: 12px; text-align: center; margin-bottom: 4px;">
      ${getOrderTypeText()}
    </div>

    ${customerNameHTML}

    <div style="border-top: 1px solid #000; margin: 6px 0;"></div>

    <!-- ITEMS HEADER -->
    <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 9px; padding: 4px 0; border-bottom: 1px solid #000;">
      <span style="flex: 1;">DESCRIPCIÓN</span>
      <span style="width: 30px; text-align: center;">CANT</span>
      <span style="width: 50px; text-align: right;">PVP</span>
    </div>

    <!-- ITEMS -->
    ${itemsHTML}

    <div style="border-top: 1px solid #000; margin: 6px 0;"></div>

    <!-- TOTALS -->
    <div style="padding: 6px 0;">
      <div style="display: flex; justify-content: space-between; padding: 2px 0; font-size: 10px;">
        <span>Base Imponible:</span>
        <span>${baseImponible.toFixed(2)}€</span>
      </div>
      <div style="display: flex; justify-content: space-between; padding: 2px 0; font-size: 10px;">
        <span>IGIC (7%):</span>
        <span>${cuotaIGIC.toFixed(2)}€</span>
      </div>
      <div style="display: flex; justify-content: space-between; padding: 8px 0; font-size: 16px; font-weight: bold; border-top: 2px double #000; border-bottom: 2px double #000; margin: 4px 0;">
        <span>TOTAL</span>
        <span>${order.total.toFixed(2)}€</span>
      </div>
    </div>

    <!-- PAYMENT METHOD - Only shown on TICKET (paid), not on CUENTA (unpaid) -->
    ${!isCuenta ? `
    <div style="text-align: center; padding: 6px 0; font-size: 11px;">
      <span>Forma de Pago: </span>
      <span style="font-weight: bold;">${getPaymentMethodText()}</span>
    </div>

    ${cashBreakdownHTML}
    ` : `
    <div style="text-align: center; padding: 10px 0; font-size: 12px; background: #fff3cd; border: 1px dashed #ffc107; margin: 6px 0;">
      <strong>⚠️ PENDIENTE DE PAGO</strong>
    </div>
    `}
    
    ${pickupInfoHTML}
    ${observationsHTML}

    <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>

    <!-- LOYALTY BLOCK -->
    <div style="text-align: center; padding: 8px 4px; margin: 6px 0; border: 1px dashed #000;">
      <div style="font-weight: bold; font-size: 11px; margin-bottom: 4px;">★ CLUB REYES ★</div>
      <div style="font-size: 10px; margin: 2px 0;">Socio: ${loyaltyMemberNumber}</div>
      <div style="font-size: 10px; margin: 2px 0;">+${pointsEarned} puntos | <strong>Total: ${totalPoints} pts</strong></div>
      <div style="font-size: 8px; color: #666;">pasteleriareyes.com/club</div>
    </div>

    <!-- Card receipts - Only on TICKET (paid) -->
    ${!isCuenta ? onlineCardReceiptHTML : ''}
    ${!isCuenta ? localCardReceiptHTML : ''}

    <!-- LEGAL FOOTER -->
    <div style="border-top: 1px dashed #000; margin: 6px 0;"></div>
    <div style="text-align: center; padding: 6px 0; font-size: 8px; line-height: 1.4;">
      <div style="padding: 4px; border: 1px solid #666; font-size: 7px; margin: 4px 0;">
        Sistema Verifactu RD 1007/2023
      </div>
      <p>IVA/IGIC incluido</p>
    </div>

    <!-- THANKS -->
    <div style="text-align: center; margin-top: 8px; padding-top: 6px; font-size: 11px; font-weight: bold;">
      ¡Gracias por su visita!
      <div style="font-size: 9px; font-weight: normal; font-style: italic;">Tradición desde 1979</div>
    </div>
  </div>
</body>
</html>`;
}

/**
 * Detect if we're on iOS/Safari (where iframe printing often fails)
 */
function isIOSDevice(): boolean {
  const userAgent = navigator.userAgent || navigator.vendor;
  return /iPad|iPhone|iPod/.test(userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Print content by opening in a new tab (iOS fallback)
 * This works reliably on iPad/Safari where iframe printing fails
 */
function printViaNewTab(htmlContent: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      // Create a blob with the HTML content
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      
      // Open in new tab
      const printWindow = window.open(blobUrl, '_blank');
      
      if (!printWindow) {
        console.warn("[printViaNewTab] Popup blocked, falling back to iframe");
        URL.revokeObjectURL(blobUrl);
        reject(new Error("Popup blocked"));
        return;
      }
      
      // Wait for content to load, then trigger print
      printWindow.onload = () => {
        setTimeout(() => {
          try {
            printWindow.focus();
            printWindow.print();
            console.log("[printViaNewTab] Print dialog triggered successfully");
            resolve();
          } catch (e) {
            console.error("[printViaNewTab] Print error:", e);
            reject(e);
          }
          
          // Cleanup after a delay (allow print dialog to complete)
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
          }, 10000);
        }, 500);
      };
      
      printWindow.onerror = () => {
        URL.revokeObjectURL(blobUrl);
        reject(new Error("Failed to load print window"));
      };
    } catch (e) {
      console.error("[printViaNewTab] Error:", e);
      reject(e);
    }
  });
}

/**
 * Print content using hidden iframe - works on most desktop browsers
 * Uses position off-screen instead of visibility:hidden for compatibility
 */
function printViaIframeInternal(htmlContent: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Create iframe off-screen but visible to DOM
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-1000px';
    iframe.style.left = '-1000px';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    // IMPORTANT: Do NOT use display:none, visibility:hidden, or 0 dimensions
    
    document.body.appendChild(iframe);
    
    const doc = iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(htmlContent);
      doc.close();
      
      // Wait for content to render, then print
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          console.log("[printViaIframe] Print dialog triggered successfully");
          resolve();
        } catch (e) {
          console.error("[printViaIframe] Error triggering print:", e);
          reject(e);
        }
        
        // Cleanup after print dialog closes
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 5000);
      }, 500);
    } else {
      document.body.removeChild(iframe);
      reject(new Error("Could not access iframe document"));
    }
  });
}

/**
 * Main print function - uses new tab on iOS/iPad, iframe on other devices
 * Exported for external use
 */
export async function printViaIframe(htmlContent: string): Promise<void> {
  // Use new tab approach on iOS devices (more reliable)
  if (isIOSDevice()) {
    console.log("[printViaIframe] iOS detected, using new tab approach");
    try {
      await printViaNewTab(htmlContent);
      return;
    } catch (e) {
      console.warn("[printViaIframe] New tab failed, falling back to iframe:", e);
    }
  }
  
  // Use iframe approach on other devices
  return printViaIframeInternal(htmlContent);
}

/**
 * High-level function to print an order ticket (paid receipt)
 */
export async function printOrderTicket(order: OrderWithLocation): Promise<void> {
  console.log("[printOrderTicket] Generating ticket HTML for order:", order.id);
  const html = generateTicketHTML(order, "ticket");
  console.log("[printOrderTicket] Printing...");
  await printViaIframe(html);
  console.log("[printOrderTicket] Print complete");
}

/**
 * High-level function to print a cuenta (unpaid bill)
 */
export async function printOrderCuenta(order: OrderWithLocation): Promise<void> {
  console.log("[printOrderCuenta] Generating cuenta HTML for order:", order.id);
  const html = generateTicketHTML(order, "cuenta");
  console.log("[printOrderCuenta] Printing...");
  await printViaIframe(html);
  console.log("[printOrderCuenta] Print complete");
}

// Interface for pickup receipt data
interface PickupReceiptData {
  order_number: number;
  customer_name: string;
  customer_phone?: string;
  pickup_date: string;
  pickup_time: string;
  pickup_location: string;
  items: Array<{
    product_name: string;
    quantity: number;
    notes?: string;
  }>;
  observations?: string;
  total: number;
  payment_status?: 'pendiente' | 'pagado' | 'pago_en_tienda';
}

/**
 * Generate HTML for a "Comprobante de Recogida" (Pickup Receipt)
 * This has a distinct design from regular tickets, focused on pickup information
 */
export function generatePickupReceiptHTML(data: PickupReceiptData): string {
  const escapeHtml = (text: string | null | undefined): string => {
    if (!text) return "";
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  // Generate items list
  const itemsHTML = data.items.map(item => `
    <div style="padding: 6px 0; border-bottom: 1px dashed #ccc;">
      <div style="display: flex; justify-content: space-between; font-size: 12px;">
        <span style="font-weight: bold;">${item.quantity}x</span>
        <span style="flex: 1; margin-left: 8px;">${escapeHtml(item.product_name)}</span>
      </div>
      ${item.notes ? `<div style="font-size: 10px; color: #e65100; margin-left: 24px; margin-top: 2px;">📝 ${escapeHtml(item.notes)}</div>` : ''}
    </div>
  `).join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Comprobante de Recogida #${data.order_number}</title>
  <style>
    @page { 
      size: 80mm auto; 
      margin: 0; 
    }
    @media print {
      body { 
        margin: 0; 
        padding: 0; 
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
    body {
      font-family: 'Courier New', 'Lucida Console', monospace;
      font-size: 11px;
      line-height: 1.3;
      color: #000;
      background: #fff;
      margin: 0;
      padding: 0;
    }
    .receipt {
      width: 80mm;
      padding: 3mm;
    }
  </style>
</head>
<body>
  <div class="receipt">
    <!-- HEADER - DISTINCT STYLE -->
    <div style="background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%); color: white; padding: 12px; text-align: center; border-radius: 4px; margin-bottom: 8px;">
      <div style="font-size: 14px; font-weight: bold; letter-spacing: 1px; margin-bottom: 4px;">
        📦 RESERVADO
      </div>
      <div style="font-size: 18px; font-weight: bold; letter-spacing: 2px;">
        COMPROBANTE DE RECOGIDA
      </div>
    </div>

    <!-- ORDER NUMBER - BIG -->
    <div style="text-align: center; padding: 12px 0; border: 3px solid #7c3aed; border-radius: 8px; margin-bottom: 12px;">
      <div style="font-size: 10px; color: #666; margin-bottom: 4px;">Nº DE ENCARGO</div>
      <div style="font-size: 36px; font-weight: bold; color: #7c3aed;">
        #${data.order_number}
      </div>
    </div>

    <!-- CUSTOMER NAME -->
    <div style="background: #f3e8ff; padding: 10px; border-radius: 4px; margin-bottom: 12px; text-align: center;">
      <div style="font-size: 10px; color: #666; margin-bottom: 2px;">CLIENTE</div>
      <div style="font-size: 16px; font-weight: bold;">${escapeHtml(data.customer_name)}</div>
      ${data.customer_phone ? `<div style="font-size: 11px; color: #666; margin-top: 2px;">📞 ${escapeHtml(data.customer_phone)}</div>` : ''}
    </div>

    <!-- PICKUP INFO - HIGHLIGHTED -->
    <div style="background: #fef3c7; border: 2px dashed #f59e0b; padding: 12px; border-radius: 4px; margin-bottom: 12px;">
      <div style="text-align: center; margin-bottom: 8px;">
        <div style="font-size: 10px; color: #92400e;">FECHA Y HORA DE RECOGIDA</div>
        <div style="font-size: 20px; font-weight: bold; color: #92400e; margin-top: 4px;">
          📅 ${escapeHtml(data.pickup_date)}
        </div>
        <div style="font-size: 18px; font-weight: bold; color: #92400e;">
          🕐 ${escapeHtml(data.pickup_time)}
        </div>
      </div>
      <div style="text-align: center; border-top: 1px dashed #f59e0b; padding-top: 8px;">
        <div style="font-size: 10px; color: #92400e;">SEDE DE ENTREGA</div>
        <div style="font-size: 14px; font-weight: bold; color: #92400e; margin-top: 2px;">
          📍 ${escapeHtml(data.pickup_location)}
        </div>
      </div>
    </div>

    <!-- PRODUCTS -->
    <div style="margin-bottom: 12px;">
      <div style="font-weight: bold; font-size: 11px; padding: 6px 0; border-bottom: 2px solid #000;">
        DETALLE DEL ENCARGO
      </div>
      ${itemsHTML}
    </div>

    ${data.observations ? `
    <!-- OBSERVATIONS -->
    <div style="background: #fef9c3; padding: 8px; border-radius: 4px; margin-bottom: 12px; font-size: 10px;">
      <strong>📝 Observaciones:</strong> ${escapeHtml(data.observations)}
    </div>
    ` : ''}

    <!-- TOTAL - Dynamic based on payment status -->
    ${data.payment_status === 'pendiente' ? `
    <div style="display: flex; justify-content: space-between; padding: 10px; background: #fee2e2; border: 2px solid #dc2626; border-radius: 4px; margin-bottom: 12px;">
      <span style="font-weight: bold; font-size: 14px; color: #dc2626;">⚠️ PENDIENTE DE PAGO:</span>
      <span style="font-weight: bold; font-size: 18px; color: #dc2626;">${data.total.toFixed(2)}€</span>
    </div>
    ` : `
    <div style="display: flex; justify-content: space-between; padding: 10px; background: #ecfdf5; border-radius: 4px; margin-bottom: 12px;">
      <span style="font-weight: bold; font-size: 14px;">TOTAL PAGADO:</span>
      <span style="font-weight: bold; font-size: 18px; color: #047857;">${data.total.toFixed(2)}€</span>
    </div>
    `}

    <!-- IMPORTANT NOTICE -->
    <div style="background: #fee2e2; border: 2px solid #ef4444; padding: 10px; border-radius: 4px; text-align: center; margin-bottom: 12px;">
      <div style="font-size: 10px; font-weight: bold; color: #b91c1c;">
        ⚠️ IMPORTANTE
      </div>
      <div style="font-size: 9px; color: #b91c1c; margin-top: 4px; line-height: 1.4;">
        Presentar este documento el día de la recogida para validar la entrega.
      </div>
    </div>

    <!-- FOOTER -->
    <div style="text-align: center; padding: 8px 0; border-top: 2px double #000;">
      <div style="font-size: 14px; font-weight: bold; margin-bottom: 2px;">
        🥐 PASTELERÍA REYES
      </div>
      <div style="font-size: 9px; color: #666;">
        Tradición desde 1979
      </div>
      <div style="font-size: 8px; color: #999; margin-top: 4px;">
        pasteleriareyes.com
      </div>
    </div>
  </div>
</body>
</html>`;
}
